import React, {useContext, useEffect} from 'react';
import {useGetRolesQuery} from '../../../services/SkillsManagement';
import LoaderSpinner from '../../../SystemDesign/LoaderSpinner';
import ListView from '../../../SystemDesign/ListView';
import {useState} from 'react';
import AddRole from './AddRole';
import ListViewMenuActions from '../../../SystemDesign/ListViewMenuActions';
import DeleteRole from './DeleteRole';
import UpdateRole from './UpdateRole';
import {SubTitleScreen, TitleScreen} from '../../../SystemDesign/Styles';
import {Typography, useMediaQuery, useTheme, Chip} from '@mui/material';
import {tokens} from '../../../theme';
import {dateTimeFormatFR, formatKey, formattedDate, isEmpty} from '../../../Utils/Functions';
import RoleChip from '../../../SystemDesign/RoleChip';
import {useDispatch, useSelector} from 'react-redux';
import {SnackBarContext} from '../../../Context/SnackBarContextProvider';
import {CLEAR_ROLES_LIST, INITIALIZE_ROLES} from '../../../Reducers/Actions';
import AssignPermissionsToRole from './AssignPermissionsToRole';
import SeeMoreRole from './SeeMoreRole';
import AssignRoleToUsers from './AssignRoleToUsers';
import DateInTable from '../../../SystemDesign/DateInTable';
import {AuthContext} from '../../../Context/AuthContext';
import SearchAndFilterBox from "../../../SystemDesign/SearchAndFilterBox";
import ListViewV2 from "../../../SystemDesign/ListViewV2";
import useDebounce from "../../../Utils/useDebounce";

const RolesList = () => {
    const dispatch = useDispatch();
    const reduxRoles = useSelector((state) => state.roles);

    const [columnFilters, setColumnFilters] = useState([]);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {setInfoAlert, setOpenSnackBar} = useContext(SnackBarContext);
    const {user, ability} = useContext(AuthContext);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearchValue = useDebounce(searchValue, 600);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Add responsive breakpoints
    const isMdOrLarger = useMediaQuery((theme) => theme.breakpoints.up("md"));
    const isSmOrLarger = useMediaQuery((theme) => theme.breakpoints.up("sm"));

    const [queryParams, setQueryParams] = useState(() => {
        // Check localStorage for saved rowsPerPage value
        const savedRowsPerPage = localStorage.getItem(`roles-rowsPerPage`);
        const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;

        return {
            filters: "",
            sorting: "",
            pagination: JSON.stringify({currentPage: 1, rowsPerPage: rowsPerPage}),
        };
    });

    const {data, error, isLoading, isFetching} = useGetRolesQuery(queryParams, {refetchOnMountOrArgChange: true});


    const handleSearch = (value) => {
        setSearchValue(value);
    }

    const actionsList = React.useMemo(() => [
        {component: SeeMoreRole, key: 'see-more'},
        ...(ability.can('update', 'Role') ? [{component: UpdateRole, key: 'update'}] : []),
        ...((ability.can('manage', 'Role') || ability.can('manage', 'Rolepermission')) ? [{
            component: AssignPermissionsToRole,
            key: 'assign-permissions'
        }] : []),
        ...(ability.can('update', 'User') ? [{component: AssignRoleToUsers, key: 'assign-role-to-users'}] : []),
        ...(ability.can('delete', 'Role') ? [{component: DeleteRole, key: 'delete'}] : []),
    ], [ability]);

    const columns = React.useMemo(() => [
        {
            id: 'name',
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => formatKey(info.getValue()),
            enableColumnFilter: false,
        },
        {
            id: 'type',
            accessorKey: 'type',
            header: 'Type',
            cell: (info) => {
                const value = info.getValue();
                if (!value) return '-';
                const labels = {
                    EMPLOYEE: 'Employee',
                    MANAGER: 'Manager',
                    ADMIN: 'Admin',
                    SUPERADMIN: 'Super Admin',
                    INSTRUCTOR: 'Instructor',
                };
                const roleColor = ({
                    EMPLOYEE: 'default',
                    MANAGER: 'info',
                    ADMIN: 'warning',
                    SUPERADMIN: 'error',
                    INSTRUCTOR: 'primary',
                }[value]) || 'default';
                const label = labels[value] || formatKey(value);
                return <Chip size="small" label={label} variant="outlined" color={roleColor}/>;
            },
            enableColumnFilter: false,
        },
        ...(isSmOrLarger ? [{
            id: 'permissionsCount',
            accessorKey: 'permissions',
            header: 'Permissions count',
            cell: (info) => {
                const count = Array.isArray(info.getValue()) ? info.getValue().length : 0;
                return <Typography variant="body2" align="center" sx={{ width: '100%' }}>{count}</Typography>;
            },
            enableColumnFilter: false,
        }] : []),
        ...(isMdOrLarger ? [{
            id: 'createdBy.username',
            accessorKey: 'createdBy',
            header: 'Created by',
            cell: (info) => {
                const createdBy = info.getValue();
                if (isEmpty(createdBy)) return 'Unknown';
                if (createdBy.system) return 'System';
                return createdBy.username || createdBy.email || 'Unknown';
            },
            enableColumnFilter: false,
        }] : []),
        ...(isMdOrLarger ? [{
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: 'Created at',
            cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))}/>,
            filterFn: 'between',
            type: "date",
        }] : []),
        ...(isMdOrLarger ? [{
            id: 'updatedAt',
            accessorKey: 'updatedAt',
            header: 'Updated at',
            cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))}/>,
            filterFn: 'between',
            type: "date",
        }] : []),
        {
            id: 'actions',
            header: '',
            size: 100,
            enableColumnFilter: false,
            cell: ({row}) => (
                <ListViewMenuActions>
                    {actionsList.map(({component: Component, key}) => (
                        <Component key={key} row={row} actionKey={key}/>
                    ))}
                </ListViewMenuActions>
            ),
        }
    ], [isMdOrLarger, isSmOrLarger, actionsList]);

    const applyFilters = (updatedFilters = columnFilters, paginationInfo = null, sortingInfo = null, searchValue = null) => {
        const filtersArray = updatedFilters || [];
        const formattedFilters = filtersArray.reduce((acc, filter) => {
            if (typeof filter === 'object' && !Array.isArray(filter)) {
                const [key, values] = Object.entries(filter)[0];

                // Fix for date filters - only include non-empty values
                if (Array.isArray(values)) {
                    // Filter out empty string values
                    const nonEmptyValues = values.filter(val => val !== "");

                    // Only add the filter if we have non-empty values
                    if (nonEmptyValues.length > 0) {
                        acc[key] = nonEmptyValues.map(val => String(val));
                    }
                } else if (values !== "") {
                    // For non-array values, still check they're not empty
                    acc[key] = [String(values)];
                }
            }
            return acc;
        }, {});

        // If paginationInfo doesn't provide rowsPerPage, check localStorage
        let rowsPerPage = paginationInfo?.rowsPerPage;
        if (!rowsPerPage) {
            const savedRowsPerPage = localStorage.getItem(`roles-rowsPerPage`);
            rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
        }

        setQueryParams(prev => ({
            ...prev,
            filters: isEmpty(formattedFilters) ? "" : JSON.stringify(formattedFilters),
            pagination: JSON.stringify({
                currentPage: paginationInfo?.currentPage || 1,
                rowsPerPage: rowsPerPage
            }),
            sorting: sortingInfo ? JSON.stringify(sortingInfo) : prev.sorting,
            search: searchValue || ""
        }));
    };

    useEffect(() => {
        if (error) {
            setInfoAlert({severity: 'error', message: error.data?.message || 'Une erreur est survenue'});
            setOpenSnackBar(true);
        }
    }, [error]);

    useEffect(() => {
        if (data?.roles) {
            dispatch({type: CLEAR_ROLES_LIST});
            dispatch({type: INITIALIZE_ROLES, payload: data.roles});
        }
    }, [data?.roles, dispatch]);

    return (
        <div>
            <Typography sx={TitleScreen(colors)}>Roles</Typography>
            <Typography sx={SubTitleScreen(colors)}>Role management</Typography>
            {ability.can('create', 'Role') && <AddRole/>}

            <LoaderSpinner loading={isLoading}/>
            {/* <SearchAndFilterBox
                searchValue={searchValue}
                handleSearch={handleSearch}
                columnFilters={columnFilters}
                setIsFilterOpen={setIsFilterOpen}
                colors={colors}
                isFetching={isFetching}
                existingFilters={false}


            /> */}
            <ListViewV2
                dispatchData={reduxRoles}
                columns={columns}
                tableName="roles"
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                applyFilters={applyFilters}
                pagination={data?.pagination}
                setQueryParams={setQueryParams}
                queryParams={queryParams}
                debouncedSearchValue={debouncedSearchValue}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}

            />

        </div>
    );
};

export default RolesList;
